import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { GridSpaceDataType, PositionType, SpaceType } from 'data/types';

export type EdgeInterval = { start: number; end: number };

const PARALLEL_EPS = 1e-6;
const COLLINEAR_DIST_EPS_FT = 1e-3;
const MIN_OVERLAP_FT = 0.05;
const COMPLEMENT_EPS = 1e-6;

function mergeIntervals(intervals: EdgeInterval[]): EdgeInterval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  let last: EdgeInterval = { ...sorted[0] };
  const merged: EdgeInterval[] = [last];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      last = { ...cur };
      merged.push(last);
    }
  }
  return merged;
}

function subtractIntervalsFromUnit(hidden: EdgeInterval[]): EdgeInterval[] {
  const merged = mergeIntervals(hidden);
  if (merged.length === 0) return [{ start: 0, end: 1 }];
  const visible: EdgeInterval[] = [];
  let cursor = 0;
  for (const h of merged) {
    if (h.start > cursor) {
      visible.push({ start: cursor, end: Math.min(1, h.start) });
    }
    cursor = Math.max(cursor, h.end);
    if (cursor >= 1) break;
  }
  if (cursor < 1) {
    visible.push({ start: cursor, end: 1 });
  }
  return visible.filter((v) => v.end - v.start > COMPLEMENT_EPS);
}

function computeCollinearOverlap(
  a1: PositionType,
  dx: number,
  dy: number,
  length: number,
  b1: PositionType,
  b2: PositionType
): EdgeInterval | null {
  const cross1 = (b1.x - a1.x) * dy - (b1.y - a1.y) * dx;
  const cross2 = (b2.x - a1.x) * dy - (b2.y - a1.y) * dx;
  const distTolerance = COLLINEAR_DIST_EPS_FT * length;
  if (Math.abs(cross1) > distTolerance) return null;
  if (Math.abs(cross2) > distTolerance) return null;

  const lenSq = length * length;
  const t1 = ((b1.x - a1.x) * dx + (b1.y - a1.y) * dy) / lenSq;
  const t2 = ((b2.x - a1.x) * dx + (b2.y - a1.y) * dy) / lenSq;

  const start = Math.max(0, Math.min(t1, t2));
  const end = Math.min(1, Math.max(t1, t2));
  const minOverlapT = MIN_OVERLAP_FT / length;
  if (end - start <= minOverlapT) return null;
  return { start, end };
}

function collectOverlapsForEdge(
  a1: PositionType,
  dx: number,
  dy: number,
  length: number,
  neighborPolygonsFt: PositionType[][]
): EdgeInterval[] {
  const intervals: EdgeInterval[] = [];
  const validNeighbors = neighborPolygonsFt.filter((n) => n.length >= 2);
  for (const neighbor of validNeighbors) {
    for (let j = 0; j < neighbor.length; j += 1) {
      const b1 = neighbor[j];
      const b2 = neighbor[(j + 1) % neighbor.length];
      const overlap = computeCollinearOverlap(a1, dx, dy, length, b1, b2);
      if (overlap) {
        intervals.push(overlap);
      }
    }
  }
  return intervals;
}

export function getHiddenIntervalsPerEdge(
  spaceVerticesFt: PositionType[],
  neighborPolygonsFt: PositionType[][]
): EdgeInterval[][] {
  if (spaceVerticesFt.length < 2) return [];
  const result: EdgeInterval[][] = spaceVerticesFt.map(() => []);

  for (let i = 0; i < spaceVerticesFt.length; i += 1) {
    const a1 = spaceVerticesFt[i];
    const a2 = spaceVerticesFt[(i + 1) % spaceVerticesFt.length];
    const dx = a2.x - a1.x;
    const dy = a2.y - a1.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq >= PARALLEL_EPS) {
      const length = Math.sqrt(lenSq);
      const intervals = collectOverlapsForEdge(
        a1,
        dx,
        dy,
        length,
        neighborPolygonsFt
      );
      result[i] = mergeIntervals(intervals);
    }
  }

  return result;
}

export function getVisibleIntervalsPerEdge(
  hidden: EdgeInterval[][]
): EdgeInterval[][] {
  return hidden.map((intervals) => subtractIntervalsFromUnit(intervals));
}

export type EdgeSegment = { p1: PositionType; p2: PositionType };

export function getVisibleEdgeSegments(
  vertices: PositionType[],
  visibleIntervalsPerEdge: EdgeInterval[][]
): EdgeSegment[] {
  const segments: EdgeSegment[] = [];
  for (let i = 0; i < vertices.length; i += 1) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % vertices.length];
    const dx = a2.x - a1.x;
    const dy = a2.y - a1.y;
    const intervals = visibleIntervalsPerEdge[i] ?? [];
    for (const interval of intervals) {
      segments.push({
        p1: { x: a1.x + dx * interval.start, y: a1.y + dy * interval.start },
        p2: { x: a1.x + dx * interval.end, y: a1.y + dy * interval.end }
      });
    }
  }
  return segments;
}

export type SpacePolygonGeometry = {
  position: { x: number; y: number; width: number; height: number };
  vertices?: PositionType[];
};

export type SpaceWithPolygon = SpacePolygonGeometry & {
  spaceId: string;
};

export type SpaceToAbsolutePolygonOptions = {
  relative?: boolean;
};

export function spaceToAbsolutePolygon(
  space: SpacePolygonGeometry,
  unitScale: number = 1,
  options?: SpaceToAbsolutePolygonOptions
): PositionType[] {
  const originX = options?.relative ? 0 : space.position.x;
  const originY = options?.relative ? 0 : space.position.y;
  if (space.vertices && space.vertices.length >= 3) {
    return space.vertices.map((v) => ({
      x: (originX + v.x) / unitScale,
      y: (originY + v.y) / unitScale
    }));
  }
  const x = originX / unitScale;
  const y = originY / unitScale;
  const w = space.position.width / unitScale;
  const h = space.position.height / unitScale;
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h }
  ];
}

export function gridSpaceVerticesToAbsoluteFeet(
  gridSpace: GridSpaceDataType
): PositionType[] {
  return spaceToAbsolutePolygon(gridSpace, STRUCTURE_GRID_SIZE);
}

export function getOpenToNeighborPolygonsAbsolute<T extends SpaceWithPolygon>(
  openToIds: string[] | undefined,
  allSpaces: T[],
  unitScale: number
): PositionType[][] {
  if (!openToIds || openToIds.length === 0) {
    return [];
  }
  const result: PositionType[][] = [];
  for (const id of openToIds) {
    const found = allSpaces.find((s) => s.spaceId === id);
    if (found) {
      result.push(spaceToAbsolutePolygon(found, unitScale));
    }
  }
  return result;
}

export function getHiddenIntervalsPerEdgeForGridSpace(
  gridSpace: GridSpaceDataType,
  allGridSpaces: GridSpaceDataType[],
  currentSpace: SpaceType | null
): EdgeInterval[][] {
  const vertexCount = gridSpace.vertices?.length ?? 4;
  if (!currentSpace) {
    return Array.from({ length: vertexCount }, () => []);
  }
  const neighborPolygons = getOpenToNeighborPolygonsAbsolute(
    currentSpace.openTo,
    allGridSpaces,
    STRUCTURE_GRID_SIZE
  );
  if (neighborPolygons.length === 0) {
    return Array.from({ length: vertexCount }, () => []);
  }
  const spaceAbs = gridSpaceVerticesToAbsoluteFeet(gridSpace);
  return getHiddenIntervalsPerEdge(spaceAbs, neighborPolygons);
}

export function spacePolygonsShareEdge(
  a: SpaceWithPolygon,
  b: SpaceWithPolygon,
  unitScale: number
): boolean {
  const polyA = spaceToAbsolutePolygon(a, unitScale);
  const polyB = spaceToAbsolutePolygon(b, unitScale);
  const hidden = getHiddenIntervalsPerEdge(polyA, [polyB]);
  return hidden.some((intervals) => intervals.length > 0);
}
